import { buildAffiliateLink } from '@/lib/affiliates/builders'
import { buildAffiliateSubId } from '@/lib/affiliates/metadata'
import { DEFAULT_VALUES } from './blogConstants'

/**
 * Определяет, какую локаль передать провайдеру
 * Берем во внимание:
 * - текущий язык статьи (ru/en)
 * - список поддерживаемых языков провайдера (поле languages в affiliateProvider)
 */
function getProviderLocale(provider, language = 'ru') {
    const fallbackRu = DEFAULT_VALUES.LOCALE.RU; // ru-RU
    const fallbackEn = DEFAULT_VALUES.LOCALE.EN; // en-US

    const langsRaw = provider?.languages;
    if (!langsRaw || typeof langsRaw !== 'string') {
        // Если у провайдера не задан список языков - ведем себя как раньше
        return language === 'ru' ? fallbackRu : fallbackEn;
    }

    const codes = langsRaw
        .split(',')
        .map(code => code.trim())
        .filter(Boolean)
        .map(code => code.toLowerCase());

    if (codes.length === 0) {
        return language === 'ru' ? fallbackRu : fallbackEn;
    }

    // Вспомогательная функция: находим лучший код под нужный базовый язык
    const pickForBaseLang = (base) => {
        // Сначала точное совпадение, напр. 'ru' или 'ru-ru'
        const exact = codes.find(c => c === base || c.startsWith(`${base}-`));
        if (exact) return exact;
        return null;
    };

    let chosenCode = null;

    if (language === 'ru') {
        // Для русской статьи сначала пробуем найти русскую локаль
        chosenCode = pickForBaseLang('ru') || pickForBaseLang('en');
    } else {
        // Для английской статьи сначала пробуем найти английскую локаль
        chosenCode = pickForBaseLang('en') || pickForBaseLang('ru');
    }

    // Если ничего подходящего не нашли - берем первый из списка провайдера
    if (!chosenCode) {
        chosenCode = codes[0];
    }

    // Нормализуем код в вид типа xx-YY
    const [langPart, countryPart] = chosenCode.split('-');
    if (!countryPart) {
        // Если провайдер указал просто 'ru' / 'en'
        if (langPart === 'ru') return fallbackRu;
        if (langPart === 'en') return fallbackEn;
        // Для других языков возвращаем как есть
        return langPart;
    }

    // Приводим к формату xx-YY (нижний регистр + верхний регистр)
    return `${langPart.toLowerCase()}-${countryPart.toUpperCase()}`;
}

/**
 * Выбирает mainPageUrl из массива offerings провайдера по приоритету:
 * country+lang → country → lang → первый.
 * Поддерживает обратную совместимость с полем mainPageUrl (если кто-то его установил).
 */
function pickOfferingUrl(provider, countrySlug, language) {
    if (provider?.mainPageUrl) return provider.mainPageUrl;

    // Пустые офферинги не считаем: у Booking для Черногории (RU) их два, и первый без URL —
    // поиск по стране промахивался и падал на «первый по языку», то есть на Албанию. Подвал
    // статей о Черногории вёл в отели Албании (ревью 30.09.2026).
    const offerings = (provider?.offerings || []).filter((o) => o?.mainPageUrl);
    if (offerings.length === 0) return null;

    const lang = (language || '').toLowerCase();
    const country = (countrySlug || '').toLowerCase();

    if (country) {
        const exact = offerings.find(o =>
            (o?.country || '').toLowerCase() === country &&
            (o?.lang || '').toLowerCase() === lang
        );
        if (exact?.mainPageUrl) return exact.mainPageUrl;

        const byCountry = offerings.find(o => (o?.country || '').toLowerCase() === country);
        if (byCountry?.mainPageUrl) return byCountry.mainPageUrl;
    }

    const byLang = offerings.find(o => (o?.lang || '').toLowerCase() === lang);
    if (byLang?.mainPageUrl) return byLang.mainPageUrl;

    return offerings[0]?.mainPageUrl || null;
}

// Шаблон Trip.com в Sanity — hotels/list?city={cityId}&countryId={countryId}…, а ссылки из
// статей знают только слаг страны. Без countryId поиск открывался пустым («Найдено 0 объектов»):
// 11 из 22 кликов Trip.com за 90 дней до 30.09.2026. С одним countryId Катар даёт 252 объекта.
// Значения — providerRefs.tripcom.tripCountryId из документов country.
const TRIP_COUNTRY_IDS = {
    montenegro: 257,
    qatar: 163,
    switzerland: 80,
    albania: 5,
    georgia: 180,
    slovenia: 84,
};

/**
 * Строит affiliate ссылку для провайдера
 * @param {Object} provider - Объект провайдера из Sanity
 * @param {string} language - Язык ('ru' или 'en')
 * @param {string|null} countrySlug - Slug страны контекста (для выбора правильного offering, например страна из поста)
 * @param {Object} [attribution] - Контекст атрибуции без PII
 * @returns {string} - Готовая ссылка или '#'
 */
export function buildProviderLink(provider, language = 'ru', countrySlug = null, attribution = {}) {
    if (!provider?.code) {
        return '#';
    }

    // Формируем affiliate ссылку на сайт провайдера - используем ту же логику, что на страницах провайдеров
    // Все данные берутся из Sanity, без fallback на дефолтные URL
    const linkMode = provider.linkMode || 'query';

    // Для режима template baseUrl не обязателен - используется шаблон из linkTemplates
    const isTemplateMode = linkMode === 'template';
    const hasTemplate = isTemplateMode && provider.linkTemplates?.[0]?.templateUrl;

    // Выбираем offering по контексту страны+языка, иначе fallback на trackingBaseUrl/redirectBaseUrl провайдера.
    const offeringUrl = pickOfferingUrl(provider, countrySlug, language);
    const baseUrl = offeringUrl || provider.trackingBaseUrl || provider.redirectBaseUrl || null;

    let href = '#';

    // Для режима template можно вызывать buildAffiliateLink без baseUrl
    // Для других режимов нужен валидный baseUrl
    const canBuildLink = isTemplateMode ? hasTemplate : (baseUrl && /^https?:\/\//i.test(baseUrl));

    if (canBuildLink) {
        try {
            // Формируем контекст только для режима template (где используются плейсхолдеры)
            // Локаль берем из настроек провайдера (поле languages) с учетом языка статьи
            const locale = getProviderLocale(provider, language);
            const currency = language === 'ru' ? DEFAULT_VALUES.CURRENCY.RU : DEFAULT_VALUES.CURRENCY.EN;

            const linkContext = isTemplateMode ? {
                locale,
                currency,
                ...(provider.code === 'trip' ? { countryId: TRIP_COUNTRY_IDS[(countrySlug || '').toLowerCase()] } : {}),
            } : undefined;

            const affiliateLink = buildAffiliateLink({
                provider,
                baseUrl: baseUrl || null, // Для template может быть null
                context: linkContext,
                subId: buildAffiliateSubId({
                    language,
                    pageSlug: attribution.pageSlug,
                    position: attribution.position,
                    variant: attribution.variant,
                }),
            });

            if (affiliateLink && (/^https?:\/\//i.test(affiliateLink) || affiliateLink.startsWith('/api/r?'))) {
                href = affiliateLink;
            } else if (baseUrl && /^https?:\/\//i.test(baseUrl)) {
                href = baseUrl;
            }
        } catch (error) {
            console.error('❌ Error building affiliate link for provider:', provider.code, error);
            if (baseUrl && /^https?:\/\//i.test(baseUrl)) {
                href = baseUrl;
            }
        }
    }
    return href;
}

/**
 * Проверяет, является ли ссылка внешней
 * @param {string} href - URL для проверки
 * @returns {boolean}
 */
export function isExternalLink(href) {
    return /^https?:\/\//i.test(href) || String(href || '').startsWith('/api/r?');
}
